/**
 * 0.38.02 — i18n cache: languages list + translations per lang.
 *
 * The single biggest bottleneck for public-form time-to-render.
 * LanguageProvider (context/LanguageContext.jsx) runs two SEQUENTIAL
 * API calls on mount (getLanguages, getTranslations), and until both
 * resolve, `loading=true`. App.jsx line 308 gates the entire public
 * app on that flag, showing "Loading…" for ~1.5s while the fetches
 * complete. Every public route — including StaticPage — waits.
 *
 * This module caches both payloads in localStorage. LanguageProvider
 * seeds useState from cache on mount → `loading=false` immediately
 * on repeat visits → App.jsx renders StaticPage right away. Fresh
 * fetches fire in the background and silently update.
 *
 * Same shape as pageCache.js and libraryCache.js. Debug logging
 * uses `[i18ncache]` prefix behind the same `?libdebug=1` flag.
 */

import { appPath } from './basePath';
import { i18nApi } from '../api/i18n';

const LANGS_STORAGE_KEY = 'gt18:i18n:languages:v1';
const TRANS_STORAGE_PREFIX = 'gt18:i18n:trans:v1:';

// Session-refresh gates — first fetch per session always hits network.
let _langsRefreshedThisSession = false;
const _transRefreshedThisSession = new Set();

// In-flight dedup.
let _langsInflight = null;
const _transInflight = new Map();

let _DEBUG = false;
try {
  if (typeof window !== 'undefined') {
    const q = new URLSearchParams(window.location.search || '');
    if (q.get('libdebug') === '1' || window.__DEBUG_LIBS === true) _DEBUG = true;
  }
} catch (_e) { /* silent */ }
function _log(msg) {
  if (!_DEBUG) return;
  const t = typeof performance !== 'undefined' && performance.now
    ? Math.round(performance.now()) : Date.now();
  // eslint-disable-next-line no-console
  console.log(`[i18ncache t=${t}ms] ${msg}`);
}

/** Read cached language list (synchronous). Returns null on miss. */
export function getCachedLanguages() {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(LANGS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch (_e) { return null; }
}

/** Read cached translations for one language (synchronous). Returns null on miss. */
export function getCachedTranslations(lang) {
  if (!lang) return null;
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(TRANS_STORAGE_PREFIX + lang);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return (parsed && typeof parsed === 'object') ? parsed : null;
  } catch (_e) { return null; }
}

/** Stale-while-revalidate fetch for the language list. First call
 *  per session always hits network. */
export function fetchLanguages() {
  if (_langsInflight) return _langsInflight;
  const cached = getCachedLanguages();
  if (cached && _langsRefreshedThisSession) {
    _log('fetchLanguages CACHE HIT (already refreshed)');
    return Promise.resolve(cached);
  }
  _log(`fetchLanguages START cache=${cached ? 'populated' : 'empty'}`);
  const t0 = typeof performance !== 'undefined' && performance.now
    ? performance.now() : Date.now();
  _langsInflight = i18nApi.getLanguages()
    .then((data) => {
      const took = Math.round((typeof performance !== 'undefined' && performance.now
        ? performance.now() : Date.now()) - t0);
      _langsRefreshedThisSession = true;
      _langsInflight = null;
      try { localStorage.setItem(LANGS_STORAGE_KEY, JSON.stringify(data)); }
      catch (e) { _log(`save FAILED: ${e && e.name}`); }
      _log(`fetchLanguages RESOLVED took=${took}ms count=${Array.isArray(data) ? data.length : 0}`);
      return data;
    })
    .catch((err) => {
      _langsInflight = null;
      _log(`fetchLanguages FAILED: ${err && err.message}`);
      if (cached) return cached;
      throw err;
    });
  return _langsInflight;
}

/** Stale-while-revalidate fetch for translations of one language. */
export function fetchTranslations(lang) {
  if (!lang) return Promise.resolve({});
  const inflight = _transInflight.get(lang);
  if (inflight) return inflight;
  const cached = getCachedTranslations(lang);
  if (cached && _transRefreshedThisSession.has(lang)) {
    _log(`fetchTranslations CACHE HIT (already refreshed) ${lang}`);
    return Promise.resolve(cached);
  }
  _log(`fetchTranslations START ${lang} cache=${cached ? 'populated' : 'empty'}`);
  const t0 = typeof performance !== 'undefined' && performance.now
    ? performance.now() : Date.now();
  const promise = i18nApi.getTranslations(lang)
    .then((data) => {
      const took = Math.round((typeof performance !== 'undefined' && performance.now
        ? performance.now() : Date.now()) - t0);
      _transRefreshedThisSession.add(lang);
      _transInflight.delete(lang);
      try { localStorage.setItem(TRANS_STORAGE_PREFIX + lang, JSON.stringify(data)); }
      catch (e) { _log(`save FAILED ${lang}: ${e && e.name}`); }
      _log(`fetchTranslations RESOLVED ${lang} took=${took}ms keys=${Object.keys(data || {}).length}`);
      return data;
    })
    .catch((err) => {
      _transInflight.delete(lang);
      _log(`fetchTranslations FAILED ${lang}: ${err && err.message}`);
      if (cached) return cached;
      throw err;
    });
  _transInflight.set(lang, promise);
  return promise;
}


/* 0.38.03 — Preflight: fire the fetches at module load, before
 * React boots. `fetchLanguages` needs no input; `fetchTranslations`
 * needs a language which we guess from localStorage (falling back
 * to the app's default). If the guess is wrong we fetch again for
 * the real language, but for the common case (user's saved language
 * matches) both fetches overlap with JS parse and provider setup.
 *
 * On a first visit where fetchTranslations takes ~5s, this shaves
 * ~500-700ms by starting during JS-parse-time instead of
 * after LanguageProvider mounts.
 */
try {
  if (typeof window !== 'undefined') {
    fetchLanguages().catch(() => { /* LanguageProvider handles */ });
    let guessLang = null;
    try { guessLang = localStorage.getItem('caf_lang'); } catch (_e) { /* silent */ }
    // Also try to pull language from a URL like `/hy/p/slug`.
    if (!guessLang) {
      try {
        const m = (appPath() || '').match(/^\/([a-z]{2})\//i);
        if (m) guessLang = m[1];
      } catch (_e) { /* silent */ }
    }
    if (guessLang) {
      fetchTranslations(guessLang).catch(() => { /* LanguageProvider handles */ });
    }
  }
} catch (_e) { /* preflight is best-effort; never break app boot */ }
